import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { apiClient } from "../../../services/apiClient";
import { useToast } from "../../../Toaster/ToastContext";
import PropTypes from "prop-types";

const AddCoupons = ({ coupon, onSave, canUpdate = true, canCreate = true }) => {
  const price = [
    { value: "choose", label: "Choose Type" },
    { value: "fixed", label: "Value" },
    { value: "percentage", label: "Percentage" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const { showToast } = useToast();
  const [status, setStatus] = useState("active");
  const [start_date, setStartDate] = useState(null);
  const [end_date, setEndDate] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [discount_type, setDiscountType] = useState(null);
  const [discount_value, setDiscountValue] = useState("");
  const [max_users, setNoOfUsers] = useState("");
  const [errors, setErrors] = useState({
    name: "",
    code: "",
    discount_type: "",
    discount_value: "",
    max_users: "",
    status: "",
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    if (coupon) {
      setName(coupon.name);
      setCode(coupon.code);
      setDiscountType(coupon.discount_type);
      setDiscountValue(coupon.discount_value);
      setNoOfUsers(coupon.max_users);
      setStatus(coupon.status);
      setStartDate(new Date(coupon.start_date));
      setEndDate(new Date(coupon.end_date));
      setErrors({
        name: "",
        code: "",
        discount_type: "",
        discount_value: "",
        max_users: "",
        status: "",
        start_date: "",
        end_date: "",
      });
    }
  }, [coupon]);

  const formatDate = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateName = (value) => {
    if (!value.trim()) {
      return "Name is required";
    }
    return "";
  };

  const validateCode = (value) => {
    if (!value.trim()) {
      return "Code is required";
    } else if (value.length > 100) {
      return "Code must not exceed 100 characters";
    }
    return "";
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    setErrors((prev) => ({
      ...prev,
      name: validateName(value),
    }));
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    setCode(value);
    setErrors((prev) => ({
      ...prev,
      code: validateCode(value),
    }));
  };

  const handleStatusChange = (selectedOption) => {
    setStatus(selectedOption.value);
    setErrors((prev) => ({
      ...prev,
      status: "",
    }));
  };

  const handleTypeChange = (selectedOption) => {
    setDiscountType(selectedOption.value);
    setErrors((prev) => ({
      ...prev,
      discount_type: "",
    }));
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setErrors((prev) => ({
      ...prev,
      start_date: "",
    }));
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setErrors((prev) => ({
      ...prev,
      end_date: "",
    }));
  };

  const handleDiscountChange = (e) => {
    const value = e.target.value;
    setDiscountValue(value);
    setErrors((prev) => ({
      ...prev,
      discount_value: value.trim() ? "" : "Discount value is required",
    }));
  };

  const handleNoOfUsersChange = (e) => {
    const value = e.target.value;
    setNoOfUsers(value);
    setErrors((prev) => ({
      ...prev,
      max_users: value.trim() ? "" : "No of Users is required",
    }));
  };

  // Function to reset all form fields
  const resetForm = () => {
    setName("");
    setCode("");
    setDiscountType(null);
    setDiscountValue("");
    setNoOfUsers("");
    setStatus("active");
    setStartDate(null);
    setEndDate(null);
    setErrors({
      name: "",
      code: "",
      discount_type: "",
      discount_value: "",
      max_users: "",
      status: "",
      start_date: "",
      end_date: "",
    });
  };

  React.useEffect(() => {
    const modalElement = document.getElementById("add-units");
    if (modalElement) {
      const handleModalHidden = () => {
        resetForm();
      };

      modalElement.addEventListener("hidden.bs.modal", handleModalHidden);
      return () => {
        modalElement.removeEventListener("hidden.bs.modal", handleModalHidden);
      };
    }
  }, []);

  const handleCreateCoupon = async () => {
    setErrors({
      name: "",
      code: "",
      discount_type: "",
      discount_value: "",
      max_users: "",
      status: "",
      start_date: "",
      end_date: "",
    });

    let isValid = true;
    const newErrors = {
      name: "",
      code: "",
      discount_type: "",
      discount_value: "",
      max_users: "",
      status: "",
      start_date: "",
      end_date: "",
    };

    if (!name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!code.trim()) {
      newErrors.code = "Code is required";
      isValid = false;
    } else if (code.length > 100) {
      newErrors.code = "Code must not exceed 100 characters";
      isValid = false;
    }

    if (!discount_type || discount_type === "choose") {
      newErrors.discount_type = "Discount Type is required";
      isValid = false;
    }

    if (!discount_value.trim()) {
      newErrors.discount_value = "Discount value is required";
      isValid = false;
    }

    if (!max_users.trim()) {
      newErrors.max_users = "No of Users is required";
      isValid = false;
    }

    if (!status) {
      newErrors.status = "Status is required";
      isValid = false;
    }

    if (!start_date) {
      newErrors.start_date = "Start Date is required";
      isValid = false;
    }

    if (!end_date) {
      newErrors.end_date = "End Date is required";
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) {
      return;
    }

    const couponData = {
      ...(coupon?.id ? { id: coupon.id } : {}),
      name: name,
      code: code,
      discount_type: discount_type,
      discount_value: discount_value,
      max_users: max_users,
      status: status,
      start_date: formatDate(start_date),
      end_date: formatDate(end_date),
    };
    try {
      // console.log('Coupon Data from AddCoupons:', couponData);
      const Apidata = await apiClient.post("/coupon/save", couponData);
      if (Apidata.success) {
        onSave && onSave();
        document.querySelector('[data-bs-dismiss="modal"]').click();
        resetForm();
        showToast(Apidata.message, "success", "Coupon Operation!");
      } else {
        showToast(Apidata.message, "error", "Coupon Operation!");
      }
    } catch (error) {
      showToast(error.message, "error", "Coupon Operation!");
    }
  };

  return (
    <div>
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-dialog-centered custom-modal-two">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>Add Coupons</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={name}
                            onChange={handleNameChange}
                          />
                          {errors.name && (
                            <div className="text-danger mt-1">
                              {errors.name}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Code</label>
                          <input
                            type="text"
                            className="form-control"
                            value={code}
                            onChange={handleCodeChange}
                          />
                          {errors.code && (
                            <div className="text-danger mt-1">
                              {errors.code}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Type</label>
                          <Select
                            className="select"
                            options={price}
                            placeholder="Choose Type"
                            value={price.find(
                              (option) => option.value === discount_type
                            )}
                            onChange={handleTypeChange}
                          />
                          {errors.discount_type && (
                            <div className="text-danger mt-1">
                              {errors.discount_type}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Discount</label>
                          <input
                            type="text"
                            className="form-control"
                            value={discount_value}
                            onChange={handleDiscountChange}
                          />
                          {errors.discount_value && (
                            <div className="text-danger mt-1">
                              {errors.discount_value}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">No of Users</label>
                          <input
                            type="text"
                            className="form-control"
                            value={max_users}
                            onChange={handleNoOfUsersChange}
                          />
                          {errors.max_users && (
                            <div className="text-danger mt-1">
                              {errors.max_users}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Start Date</label>
                          <div className="input-groupicon calender-input">
                            <DatePicker
                              className="form-control filterdatepicker"
                              selected={start_date}
                              onChange={handleStartDateChange}
                              dateFormat="dd-MM-yyyy"
                              placeholderText="DD-MM-YYYY"
                            />
                            {errors.start_date && (
                              <div className="text-danger mt-1">
                                {errors.start_date}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>End Date</label>
                          <div className="input-groupicon calender-input">
                            <DatePicker
                              className="form-control filterdatepicker"
                              selected={end_date}
                              onChange={handleEndDateChange}
                              dateFormat="dd-MM-yyyy"
                              placeholderText="DD-MM-YYYY"
                            />
                            {errors.end_date && (
                              <div className="text-danger mt-1">
                                {errors.end_date}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <Select
                            className="select"
                            options={statusOptions}
                            value={statusOptions.find(
                              (option) => option.value === status
                            )}
                            onChange={handleStatusChange}
                            placeholder="Select Status"
                          />
                          {errors.status && (
                            <div className="text-danger mt-1">
                              {errors.status}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* <div className="modal-footer-btn">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <Link to="#" className="btn btn-submit" onClick={handleCreateCoupon}>
                        Create Coupon
                      </Link>
                    </div> */}
                    <div className="modal-footer-btn">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>

                      {coupon
                        ? canUpdate && (
                            <Link
                              to="#"
                              className="btn btn-submit"
                              onClick={handleCreateCoupon}
                            >
                              Update Coupon
                            </Link>
                          )
                        : canCreate && (
                            <Link
                              to="#"
                              className="btn btn-submit"
                              onClick={handleCreateCoupon}
                            >
                              Create Coupon
                            </Link>
                          )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCoupons;

AddCoupons.propTypes = {
  coupon: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    code: PropTypes.string,
    discount_type: PropTypes.string,
    discount_value: PropTypes.string,
    max_users: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    status: PropTypes.oneOf(["Active", "Inactive", ""]),
  }),
  onSave: PropTypes.func,
  canUpdate: PropTypes.bool,
  canCreate: PropTypes.bool,
};

AddCoupons.defaultProps = {
  coupon: null,
};
